import { create } from '../../../utils/createStore.js';

/**
 * Efforts dashboard filter selection.
 *
 * Unlike the shared single-value `useFilters` store (still used by the
 * Division Performance dashboard), Efforts supports MULTI-SELECT for Division,
 * State and HQ/Territory, plus scalar Period (yearmonth), Designation (role)
 * and Tenure Band. The backend accepts comma-separated lists for the
 * multi-selects (empty list = no filter) and optional scalar params for the
 * rest. To avoid changing the shared store's contract and breaking other
 * dashboards, Efforts owns this dedicated store.
 *
 * Selections are "applied" explicitly (via the filter bar's Apply button), so
 * this store holds only the committed selection the hooks read from. The draft
 * (pre-Apply) selection lives locally in the filter bar component.
 *
 * @typedef {Object} EffortsFilterSelection
 * @property {string[]} division
 * @property {string[]} state
 * @property {string[]} territory
 * @property {string|null} yearmonth - Period as YYYYMM; null = backend default (latest).
 * @property {string[]} role - Designations: FLS | FLM | SLM | TLM. Default ['FLS'].
 * @property {string[]} tenureBand - lt6m | 6to12m | 1to3y | gt3y. Default [] (= All).
 */

/** Default designation selection when nothing is chosen. */
export const DEFAULT_EFFORTS_ROLE = 'FLS';

/** @type {EffortsFilterSelection} */
const emptySelection = {
  division: [],
  state: [],
  territory: [],
  yearmonth: null,
  role: [DEFAULT_EFFORTS_ROLE],
  tenureBand: [],
};

/**
 * Zustand store holding the APPLIED Efforts filter selection.
 */
export const useEffortsFilters = create((set) => ({
  /** @type {EffortsFilterSelection} */
  applied: { ...emptySelection },

  /**
   * Commit a complete selection (called on Apply).
   * @param {EffortsFilterSelection} selection
   */
  applySelection: (selection) =>
    set({
      applied: {
        division: [...(selection.division ?? [])],
        state: [...(selection.state ?? [])],
        territory: [...(selection.territory ?? [])],
        yearmonth: selection.yearmonth ?? null,
        role: [...(selection.role ?? [])],
        tenureBand: [...(selection.tenureBand ?? [])],
      },
    }),

  /** Reset applied selection to defaults (called on Reset). */
  resetSelection: () => set({ applied: { ...emptySelection } }),
}));

export { emptySelection as EMPTY_EFFORTS_SELECTION };
